import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import StoreIcon from '@mui/icons-material/Store';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import LogoutIcon from '@mui/icons-material/Logout';
import CategoryIcon from '@mui/icons-material/Category';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EmailIcon from '@mui/icons-material/Email';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCaja } from '../context/CajaContext';

const MENU_SECTIONS = [
  {
    key: 'chat',
    label: 'Chat',
    icon: ChatBubbleOutlineIcon,
    roles: ['admin', 'viewer', 'cajero', 'bodega'],
    defaultOpen: false,
    items: [{ to: '/inbox', label: 'Inbox Telegram', icon: ChatBubbleOutlineIcon }],
  },
  {
    key: 'catalogos',
    label: 'Catalogos',
    icon: CategoryIcon,
    roles: ['admin', 'viewer', 'cajero', 'bodega'],
    defaultOpen: false,
    items: [
      { to: '/clientes', label: 'Clientes', icon: PeopleIcon },
      { to: '/proveedores', label: 'Proveedores', icon: LocalShippingIcon },
      { to: '/productos', label: 'Productos', icon: StoreIcon },
      { to: '/marcas', label: 'Marcas', icon: LocalOfferIcon },
      { to: '/categorias-catalogo', label: 'Categorías', icon: CategoryIcon, roles: ['admin'] },
      {
        to: '/servicios-catalogo',
        label: 'Catálogo servicios',
        icon: MiscellaneousServicesIcon,
        roles: ['admin'],
      },
    ],
  },
  {
    key: 'operaciones',
    label: 'Operaciones',
    icon: TrendingUpIcon,
    roles: ['admin', 'viewer', 'cajero'],
    defaultOpen: false,
    items: [
      { to: '/compras', label: 'Compras', icon: ShoppingCartIcon, roles: ['admin'] },
      { to: '/ventas', label: 'Ventas', icon: TrendingUpIcon },
      { to: '/pendientes-anulacion', label: 'Pendientes/Anulación', icon: AssignmentReturnIcon, roles: ['admin', 'cajero'] },
      { to: '/abonos', label: 'Abonos', icon: PaymentsIcon },
      { to: '/caja', label: 'Caja', icon: PointOfSaleIcon, roles: ['admin', 'cajero'] },
      { to: '/servicios-ventas', label: 'Servicios', icon: BuildIcon, roles: ['admin', 'cajero'] },
      {
        to: '/servicios-digitales',
        label: 'Servicios digitales',
        icon: VpnKeyIcon,
        roles: ['admin', 'cajero', 'viewer'],
      },
    ],
  },
  {
    key: 'reparaciones',
    label: 'Reparaciones',
    icon: BuildIcon,
    roles: ['admin', 'viewer'],
    defaultOpen: false,
    items: [
      { to: '/reparaciones/ingresar', label: 'Ingresar equipo', icon: AssignmentReturnIcon },
      { to: '/reparaciones/seguimiento', label: 'Seguimiento', icon: BuildIcon },
    ],
  },
  {
    key: 'reportes',
    label: 'Reportes',
    icon: AssessmentIcon,
    roles: ['admin', 'viewer'],
    defaultOpen: false,
    items: [{ to: '/reporteVentas', label: 'Ventas por fecha', icon: ReceiptIcon }],
  },
  {
    key: 'administracion',
    label: 'Administración',
    icon: AdminPanelSettingsIcon,
    roles: ['admin'],
    defaultOpen: false,
    items: [
      { to: '/usuarios', label: 'Usuarios', icon: PeopleIcon },
      { to: '/cuentas-webmail', label: 'Cuentas webmail', icon: EmailIcon },
      { to: '/configuracion-negocio', label: 'Negocio y Ráfaga', icon: StoreIcon },
    ],
  },
];

const Menu = ({
  variant = 'persistent',
  open = false,
  onClose,
  width = 264,
  appBarHeight = 64,
  onNavigateClose,
}) => {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { cajaAbierta, cajaRequired } = useCaja();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isCajaLocked = cajaRequired && !cajaAbierta;

  const allowedSections = useMemo(
    () =>
      MENU_SECTIONS.filter((section) => (section.roles ? section.roles.includes(user?.role) : true)).map(
        (section) => ({
          ...section,
          items: section.items.filter((item) => (item.roles ? item.roles.includes(user?.role) : true)),
        })
      ),
    [user?.role]
  );

  const [openMap, setOpenMap] = useState(() =>
    Object.fromEntries(MENU_SECTIONS.map((section) => [section.key, Boolean(section.defaultOpen)]))
  );

  const handleLogout = () => {
    logout();
    onNavigateClose?.();
    navigate('/');
  };

  const toggleSection = (key) =>
    setOpenMap((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  const NavItem = ({ to, icon: IconComponent, label }) => (
    <ListItemButton
      component={Link}
      to={to}
      onClick={onNavigateClose}
      selected={pathname === to}
      sx={{
        borderRadius: 2,
        mx: 1,
        mt: 0.5,
        '&.Mui-selected': {
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
          '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
          fontWeight: 600,
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
        <IconComponent fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={label} />
    </ListItemButton>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      slots={{transition:Fade}}
      slotProps={{paper:{id:'menu-principal',component:'nav','aria-label':'Menú principal'}}}
      transitionDuration={260}
      ModalProps={{ keepMounted: true }}
      sx={{
        width:variant==='temporary' ? 0 : open ? width : 0,
        flexShrink: 0,
        transition:theme.transitions.create('width',{duration:260,easing:theme.transitions.easing.easeInOut}),
        '@media (prefers-reduced-motion: reduce)':{transition:'none'},
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          pointerEvents:open ? 'auto' : 'none',
          pt: `${appBarHeight}px`,
          backgroundImage:
            theme.palette.mode === 'light'
              ? 'linear-gradient(180deg, #f8fafc, #ffffff)'
              : 'linear-gradient(180deg, #0f172a, #1e1b4b)',
        },
      }}
    >
      <Box sx={{ px: 2, pb: 3 }}>
        <Stack
          spacing={0.5}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor:
              theme.palette.mode === 'light'
                ? alpha(theme.palette.primary.main, 0.08)
                : alpha(theme.palette.primary.main, 0.18),
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Estado general
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Operativo
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Gestiona catalogos y operaciones desde aqui.
          </Typography>
        </Stack>
      </Box>

      <Divider />

      <List sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {isCajaLocked ? (
          <Box sx={{ mb: 1, mt: 1 }}>
            <NavItem to="/caja" label="Aperturar caja" icon={PointOfSaleIcon} />
          </Box>
        ) : (
          <>
          <NavItem to="/dashboard" label="Zona de Ráfaga" icon={PointOfSaleIcon} />
          {allowedSections.map((section) => {
            const SectionIcon = section.icon;
            return (
              <Box key={section.key} sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => toggleSection(section.key)}
                  sx={{
                    borderRadius: 0,
                    px: 2,
                    py: 1.2,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  <ListItemIcon sx={{ color: 'text.secondary' }}>
                    <SectionIcon />
                  </ListItemIcon>
                  <ListItemText primary={section.label} />
                  {openMap[section.key] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openMap[section.key]} timeout="auto" unmountOnExit>
                  {section.items.map((item) => (
                    <NavItem key={item.to} {...item} />
                  ))}
                </Collapse>
              </Box>
            );
          })}
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ my: 1 }} />

        <ListItemButton onClick={handleLogout} sx={{ mx: 2, borderRadius: 2 }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Cerrar sesion" />
        </ListItemButton>
      </List>
    </Drawer>
  );
};

export default Menu;
